/** @jsxImportSource @emotion/react */
import { HTMLAttributes } from 'react'
import { getHeaderContentHeight } from '@signal/layouts/utils'
import HeaderBase from '@signal/layouts/Header'
import Layout from '@signal/layouts/Layout'
import estyled, { ecss } from '@signal/theme/emotion'
import {
  appBarZIndexSelector,
  getSpacingSelector,
  paletteDividerSelector,
  getColorBackgroundSelector
} from '@signal/theme/selectors'
import { AnyReactComponentType } from '@signal/utils/types'
import Brand from '../Brand'
import Typography from '../Typography'
import { TypographyPropsType } from '../Typography/types'

export const Wrapper = estyled<{
  constrainWidth: boolean
  left: AnyReactComponentType
  adjustPageContainerForMenu: boolean
  disableGutter: boolean
}>(Layout)`
  height: 100%;
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
`

export const ChildWrapper = estyled.div<{
  appMinWidth?: number
  children?: React.ReactNode
}>`
  ${({ appMinWidth }) => (appMinWidth ? ecss`min-width: ${appMinWidth}px;` : '')}
`

export const ScrollWrapper = estyled.div<{
  appMinWidth?: number
  fromBaseline?: boolean
  hideChrome?: boolean
  hideFooter?: boolean
  children?: React.ReactNode
}>`
  ${({ appMinWidth, fromBaseline }) =>
    /* also hide the y-overflow to prevent a small y-scrollbar to encompass where the x-scrollbar was rendered */
    appMinWidth
      ? ecss`overflow-x: auto; ${fromBaseline ? 'overflow-y: hidden;' : ''}`
      : ''}
  ${({ fromBaseline }) => (!fromBaseline ? `height: calc(100vh - ${getHeaderContentHeight(true)});` : '')}
`

export const Header = estyled<{
  constrainWidth?: boolean
  fixed?: boolean
  left?: AnyReactComponentType
}>(HeaderBase)`
  background-color: ${getColorBackgroundSelector('paper')};
  border-bottom: 1px solid ${
    /* @ts-ignore */
    paletteDividerSelector
  };
  z-index: ${appBarZIndexSelector};
  @media print {
    display: none;
  }
`

export const AppIconWrapper = estyled.div`
  display: flex;
  margin-left: ${getSpacingSelector(3)}px;
`

export const Left = estyled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  padding-left: ${getSpacingSelector(3)}px;
  flex: 1;
`

export const AppTitle = estyled<TypographyPropsType>(Typography).attrs(() => ({
  variant: 'h4',
  color: 'textDark'
}))`
  margin-right: ${getSpacingSelector(9)}px;
  align-self: center;
`

export const Middle = estyled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  align-items: center;
`

interface BrandingWrapperProps extends HTMLAttributes<HTMLDivElement> {
  clickable?: boolean
  selected?: boolean
  selectedColor?: string
}

export const BrandingWrapper = estyled.div<BrandingWrapperProps>`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  transition: background 0.15s;
  border-radius: 16px;
  &.header-branding {
    border-radius: 0px;
    clip-path: inset(0px 0px 10px 0px);
  }
  ${({ selected = false, selectedColor }) =>
    selected &&
    ecss`
      background: ${
        selectedColor
          ? `${selectedColor}26` // 15% opacity in hex
          : 'rgba(0, 0, 0, 0.06)'
      };
    `}
  ${({ clickable = false, selected = false, selectedColor }) =>
    clickable &&
    !selected &&
    ecss`
      &:hover {
        background: ${
          selectedColor
            ? `${selectedColor}26` // 15% opacity in hex
            : 'rgba(0, 0, 0, 0.06)'
        };
      }
    `}
`

export const PureCarsBrand = estyled<{
  height?: number
  width?: number
  src: string
}>(Brand)``
